import React from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../../config/localization';
import Icon, { ICONS } from '../../ui/icons';
import GalleryFiltersWidget from '../../widgets/GalleryFiltersWidget';
import type { GalleryFilters } from '../../pages/gallery/GalleryContent';

interface GalleryFiltersContextProps {
  selectedLanguage: Language;
  filters: GalleryFilters;
  onFiltersChange: (filters: Partial<GalleryFilters>) => void;
}

const GalleryFiltersContext: React.FC<GalleryFiltersContextProps> = ({
  selectedLanguage,
  filters,
  onFiltersChange
}) => {
  const { currentOrganization } = useOrganization();
  const t = useLocalization(selectedLanguage);

  // Check if filters are active (not placeholder)
  const isFilteringActive = onFiltersChange.toString() !== '() => {}';

  // Show fallback when not in active gallery filtering mode
  if (!isFilteringActive) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <Icon name={ICONS.GALLERY} size={20} className="text-purple-400" />
            {t.galleryPage?.title || 'Gallery'}
          </h3>
        </div>

        {/* Inactive state message */}
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div>
            <Icon name={ICONS.GALLERY} size={48} className="mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400">
              {t.galleryPage?.filtersAvailableInGallery || 'Filters are available when viewing the gallery'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Widget Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <GalleryFiltersWidget
            language={selectedLanguage}
            filters={filters}
            onFiltersChange={onFiltersChange}
            organizationId={currentOrganization?.id}
            widgetId="gallery_filters_widget"
          />
        </div>
      </div>

      {/* Trash Button - Fixed at bottom */}
      <div className="p-3 border-t border-gray-700/50">
        <button
          onClick={() => onFiltersChange({ ...filters, showTrashed: !filters.showTrashed })}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
            filters.showTrashed
              ? 'bg-red-600/20 border border-red-500/40 text-red-300'
              : 'bg-gray-700/30 border border-gray-600/30 text-gray-400 hover:bg-gray-600/40 hover:text-gray-300'
          }`}
        >
          <Icon
            name={ICONS.TRASH}
            size={14}
            className={filters.showTrashed ? 'text-red-400' : 'text-gray-500'}
          />
          <span className="flex-1 text-left">
            {t.gallery?.trash || 'Trash'}
          </span>
          {filters.showTrashed && (
            <Icon name={ICONS.CHECK} size={12} className="text-red-400" />
          )}
        </button>
      </div>
    </div>
  );
};

export default GalleryFiltersContext;